import React from 'react';
import { ArrowUpRight, Share2 } from 'lucide-react';
import './HomeThreadPreview.css';

// An abstracted thread page, drawn the way the real one reads: the
// "In this thread" checklist with its meter on the left; the style header
// (cover, code, stage, colorway chips) and the stacked sections — Design,
// Colorways, Tech pack, Photoshoot — with the app's uppercase labels,
// hairline rules, image decks with counts and "Open in …" links. Static:
// no auth, no API; the only links open the real sample files.
const Deck = ({ src, alt, width, height, count, ratio = '3 / 4', fit = 'contain' }) => (
  <span className={`ht-deck ht-deck-${fit}`} style={{ aspectRatio: ratio }}>
    {count > 2 && <span className="ht-deck-card back2" aria-hidden="true" />}
    {count > 1 && <span className="ht-deck-card back1" aria-hidden="true" />}
    <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async" />
    {count > 1 && <span className="ht-deck-count" aria-label={`${count} files`}>{count}</span>}
  </span>
);

const SectionHead = ({ label, meta, link }) => (
  <div className="ht-sec-head">
    <span className="ht-sec-label">{label}{meta ? ` · ${meta}` : ''}</span>
    <span className="ht-sec-rule" aria-hidden="true" />
    {link && <span className="ht-sec-link">{link} →</span>}
  </div>
);

export default function HomeThreadPreview({ content }) {
  const { preview } = content;
  const [techpack, materials] = preview.documents;
  const cover = preview.colorways[0];

  return (
    <section id="thread" className="home-thread" aria-labelledby="home-thread-title">
      <div className="home-thread-inner">
        <div className="home-thread-intro">
          <div>
            <div className="home-thread-eyebrow">
              <p className="lp-eyebrow">{content.eyebrow}</p>
              <span>{content.badge}</span>
            </div>
            <h2 id="home-thread-title" className="lp-h2">{content.h2}</h2>
          </div>
          <p className="home-thread-description">{content.body}</p>
        </div>

        <figure className="home-thread-example" aria-label={preview.label}>
          <div className="ht-page">
            {/* ---------- the checklist rail ---------- */}
            <aside className="ht-rail" aria-label="In this thread">
              <p className="ht-rail-head">IN THIS THREAD</p>
              <ul className="ht-rail-list">
                {preview.rail.map((row) => (
                  <li key={row.label} className={`ht-rail-row ${row.state}`}>
                    <span className={`ht-glyph ${row.state}`} aria-hidden="true">
                      {row.state === 'done' ? '✓' : row.state === 'inflight' ? row.glyph : ''}
                    </span>
                    <span className="ht-rail-label">{row.label}</span>
                    {row.meta && <span className="ht-rail-meta">{row.meta}</span>}
                    <span className="ht-sr">{row.state === 'done' ? 'done' : row.state === 'inflight' ? 'in flight' : 'open'}</span>
                  </li>
                ))}
              </ul>
              <div className="ht-meter" aria-hidden="true">
                {preview.rail.map((row) => <span key={row.label} className={`ht-meter-seg ${row.state}`} />)}
              </div>
              <p className="ht-meter-line">{preview.meter}</p>
              <p className="ht-rail-share"><Share2 size={13} aria-hidden="true" />Share this thread</p>
            </aside>

            {/* ---------- the style page ---------- */}
            <div className="ht-main">
              <header className="ht-head">
                <img className="ht-cover" src={cover.src} alt="" width={cover.width} height={cover.height} loading="lazy" decoding="async" />
                <div className="ht-head-text">
                  <div className="ht-head-row">
                    <h3>{preview.styleName}</h3>
                    <span className="ht-code">{preview.styleCode}</span>
                    <span className="ht-stage">{preview.stage}</span>
                  </div>
                  <p className="ht-head-meta">{preview.collection} · {preview.meta.split(' · ').slice(1).join(' · ')}</p>
                  <p className="ht-head-counts">{preview.counts}</p>
                  <div className="ht-lanes" aria-label="Colorways">
                    <span className="ht-lanes-label">COLORWAYS</span>
                    <span className="ht-lane on">All</span>
                    {preview.colorways.map((colorway) => (
                      <span key={colorway.name} className="ht-lane">
                        <i style={{ backgroundColor: colorway.color }} aria-hidden="true" />{colorway.name}
                      </span>
                    ))}
                  </div>
                </div>
              </header>

              <div className="ht-tabs" aria-hidden="true"><span className="on">Overview</span><span>All files</span></div>

              <div className="ht-sec-row">
                <section className="ht-sec" aria-label={preview.sections.design.label}>
                  <SectionHead label={preview.sections.design.label.toUpperCase()} link={preview.sections.design.link} />
                  <div className="ht-card ht-design">
                    <div className="ht-design-deck">
                      <Deck src={preview.original.src} alt={preview.original.alt} width={preview.original.width} height={preview.original.height} count={preview.original.count} />
                    </div>
                    <div className="ht-kicker">
                      <span className="ht-kicker-label">{preview.original.label.toUpperCase()}</span>
                      <span className="ht-kicker-sub">{preview.original.caption}</span>
                    </div>
                  </div>
                </section>

                <section className="ht-sec" aria-label={preview.sections.shoot.label}>
                  <SectionHead label={preview.sections.shoot.label.toUpperCase()} meta={`${preview.photo.count} shot`} link={preview.sections.shoot.link} />
                  <div className="ht-card ht-shoot">
                    <a className="ht-shot" href={preview.photo.src} target="_blank" rel="noopener noreferrer">
                      <Deck src={preview.photo.src} alt={preview.photo.alt} width={preview.photo.width} height={preview.photo.height} count={1} fit="cover" />
                      <span className="ht-colorway-name">{preview.photo.caption}<ArrowUpRight size={13} aria-hidden="true" /></span>
                    </a>
                    <div className="ht-kicker">
                      <span className="ht-kicker-label">{preview.photo.kicker.toUpperCase()}</span>
                      <span className="ht-kicker-sub">{preview.photo.note}</span>
                    </div>
                  </div>
                </section>
              </div>

              <section className="ht-sec" aria-label={preview.sections.colorways.label}>
                <SectionHead label={preview.sections.colorways.label.toUpperCase()} meta={preview.colorways.length} link={preview.sections.colorways.link} />
                <div className="ht-scheme">
                  <span className="ht-scheme-label">{preview.scheme.label.toUpperCase()}</span>
                  {preview.scheme.chips.map(([name, hex, part]) => (
                    <span key={`${name}-${part}`} className="ht-chip"><i style={{ backgroundColor: hex }} aria-hidden="true" />{name}<small>· {part}</small></span>
                  ))}
                </div>
                <div className="ht-card ht-colorways-card">
                  <div className="ht-colorways">
                    {preview.colorways.map((colorway) => (
                      <a key={colorway.name} className="ht-colorway" href={colorway.src} target="_blank" rel="noopener noreferrer">
                        <Deck src={colorway.src} alt={colorway.alt} width={colorway.width} height={colorway.height} count={colorway.count} ratio="1 / 1" />
                        <span className="ht-colorway-name"><i style={{ backgroundColor: colorway.color }} aria-hidden="true" />{colorway.name}<ArrowUpRight size={13} aria-hidden="true" /></span>
                      </a>
                    ))}
                  </div>
                  <figure className="ht-sheet ht-scheme-sheet">
                    <Deck src={preview.scheme.sheet.src} alt={preview.scheme.sheet.alt} width={preview.scheme.sheet.width} height={preview.scheme.sheet.height} count={1} ratio="3 / 2" />
                    <figcaption><span>{preview.scheme.sheet.label}</span><small>{preview.scheme.sheet.caption}</small></figcaption>
                  </figure>
                </div>
              </section>

              <section className="ht-sec" aria-label={preview.sections.techpack.label}>
                <SectionHead label={preview.sections.techpack.label.toUpperCase()} link={preview.sections.techpack.link} />
                <div className="ht-card">
                  <div className="ht-sheets">
                    {[techpack, materials].map((document) => (
                      <figure key={document.src} className="ht-sheet">
                        <Deck src={document.src} alt={document.alt} width={document.width} height={document.height} count={1} ratio="3 / 2" />
                        <figcaption><span>{document.label}</span><small>{document.caption}</small></figcaption>
                      </figure>
                    ))}
                  </div>
                  <a className="ht-file-link" href={techpack.href} target="_blank" rel="noopener noreferrer">
                    {techpack.linkText}<ArrowUpRight size={14} aria-hidden="true" />
                  </a>
                </div>
              </section>

            </div>
          </div>
          <figcaption>{preview.caption}</figcaption>
        </figure>
      </div>
    </section>
  );
}
